import { Link } from "react-router-dom";
import { Facebook, Linkedin, Twitter, Mail, MapPin } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";

const socialLinks = [
  { icon: Facebook, href: "https://facebook.com/outsprinter", label: "Facebook" },
  { icon: Linkedin, href: "https://linkedin.com/company/outsprinter", label: "LinkedIn" },
  { icon: Twitter, href: "https://twitter.com/outsprinter", label: "Twitter" },
];

export function Footer() {
  const { t } = useTranslation();
  const { localePath } = useLanguage();

  const footerLinks = {
    product: [
      { label: t('footer.features'), href: localePath('/#features') },
      { label: t('footer.pricing'), href: localePath('/pricing') },
      { label: t('footer.updates'), href: localePath('/updates') },
    ],
    useCases: [
      { label: t('useCases.smallStartups'), href: localePath('/use-cases/small-startups') },
      { label: t('useCases.smb'), href: localePath('/use-cases/small-medium-companies') },
      { label: t('useCases.enterprise'), href: localePath('/use-cases/enterprise') },
      { label: t('useCases.itTeams'), href: localePath('/use-cases/it-teams') },
      { label: t('useCases.operations'), href: localePath('/use-cases/operations') },
      { label: t('useCases.sales'), href: localePath('/use-cases/sales') },
      { label: t('useCases.managementBoard'), href: localePath('/use-cases/management-board') },
      { label: t('useCases.projectManagement'), href: localePath('/use-cases/project-management') },
      { label: t('useCases.publicInstitutions'), href: localePath('/use-cases/public-institutions') },
      { label: t('useCases.nonprofits'), href: localePath('/use-cases/nonprofits') },
      { label: t('useCases.hrTeams'), href: localePath('/use-cases/hr-teams') },
      { label: t('useCases.manufacturing'), href: localePath('/use-cases/manufacturing') },
    ],
    resources: [
      { label: t('footer.blog'), href: localePath('/blog') },
      { label: t('footer.helpCenter'), href: localePath('/help-center') },
      { label: t('footer.faq'), href: localePath('/faq') },
    ],
    company: [
      { label: t('footer.aboutUs'), href: localePath('/about') },
      { label: t('footer.consultingAdvisory'), href: localePath('/consulting') },
      { label: t('footer.contact'), href: localePath('/contact') },
      { label: t('footer.press'), href: localePath('/press') },
      { label: t('footer.affiliates'), href: localePath('/affiliates') },
    ],
    legal: [
      { label: t('footer.termsOfService'), href: localePath('/terms') },
      { label: t('footer.privacyNotice'), href: localePath('/privacy') },
      { label: t('footer.cookiePolicy'), href: localePath('/cookie-policy') },
    ],
  };

  return (
    <footer className="bg-card border-t border-border">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-8 lg:gap-12">
          {/* Logo & Company Info */}
          <div className="col-span-2">
            <Link to={localePath('/')} className="flex items-center gap-2 mb-4">
              <img src="/logo-optimized.webp" alt="" width={150} height={100} className="h-8 w-auto" />
              <span className="font-bold text-xl text-foreground">Outsprinter</span>
            </Link>
            <p className="text-sm text-muted-foreground mb-4 max-w-xs">
              {t('footer.description')}
            </p>
            
            {/* Contact Info */}
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:info@outsprinter.com" className="hover:text-foreground transition-colors">
                  info@outsprinter.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>Tallinn, Estonia</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-3 mt-6">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                    aria-label={social.label}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Product Links */}
          <div>
            <p className="font-semibold text-foreground mb-4">{t('footer.product')}</p>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Use Cases Links */}
          <div>
            <p className="font-semibold text-foreground mb-4">{t('footer.useCases')}</p>
            <ul className="space-y-3">
              {footerLinks.useCases.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <p className="font-semibold text-foreground mb-4">{t('footer.resources')}</p>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <p className="font-semibold text-foreground mb-4">{t('footer.company')}</p>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <p className="font-semibold text-foreground mb-4">{t('footer.legal')}</p>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <Separator />

      {/* Bottom Bar */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            {t('footer.copyright')}
          </p>
          
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              ⚡ {t('footer.uptime')}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
